<template>
  <div class="auto-messages-container">
    <b-row class="mb-3">
      <b-col class="m3" sm="12" lg="8">
        <h2>Auto Message Rules</h2>
        <p class="">Define automatic internal messages sent when lane closure requests meet specific conditions.</p>
      </b-col>
      <b-col  class="m3" sm="12" lg="4" cols="auto">
        <b-button variant="primary" @click="showAddModal" :disabled="loading">
          <i class="fas fa-plus"></i> Add Auto Message Rule
        </b-button>
      </b-col>
    </b-row>

    <!-- Loading Spinner -->
    <div v-if="loading" class="text-center my-4">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2">Loading auto message rules...</p>
    </div>

<b-table
  v-else
  :items="safeRules"
  :fields="tableFields"
  striped
  hover
  :empty-text="emptyText"
  show-empty
  stacked="md"
>
  <template #cell(condition)="data">
    <span class="condition-label">
      {{ getConditionLabel(data.item.conditionKey) }}
    </span>
  </template>

  <template #cell(whenTrigger)="data">
    <b-badge :variant="data.item.whenTrigger === 'submitted' ? 'success' : 'warning'">
      When {{ data.item.whenTrigger | capitalize }}
    </b-badge>
  </template>

  <template #cell(messagePreview)="data">
    <div class="message-preview" v-html="getMessagePreview(data.item.messageHtml)"></div>
  </template>

  <template #cell(actions)="data">
    <b-button-group size="sm">
      <b-button variant="outline-primary" @click="editRule(data.item)" :disabled="saving">
        <i class="fas fa-edit"></i> Edit
      </b-button>
      <b-button variant="outline-danger" @click="deleteRule(data.index)" :disabled="saving">
        <i class="fas fa-trash"></i> Delete
      </b-button>
    </b-button-group>
  </template>
</b-table>



    <!-- Modal -->
    <b-modal
      :id="modalId"
      :title="isEditing ? 'Edit Auto Message Rule' : 'Add Auto Message Rule'"
      size="lg"
      @hidden="resetForm"
      @ok="saveRule"
      :ok-disabled="saving"
      no-close-on-backdrop
    >
      <b-form @submit.prevent="saveRule">
        <b-form-group label="Condition" label-for="condition-select">
          <b-form-select
            id="condition-select"
            v-model="currentRule.conditionKey"
            :options="conditionOptions"
            :state="formState.conditionKey"
            required
          ></b-form-select>
          <b-form-invalid-feedback>Please select a condition</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Send Message When" label-for="when-select">
          <b-form-radio-group
            id="when-select"
            v-model="currentRule.whenTrigger"
            :options="whenOptions"
            required
          ></b-form-radio-group>
        </b-form-group>

        <b-form-group label="Message Content">
          <div class="message-editor-container">
            <wysiwyg 
              v-model="currentRule.messageHtml" 
              ref="messageEditor" 
              :height="'300px'"
            />
          </div>
        </b-form-group>

        <b-form-group label="Preview">
          <div class="message-preview-box p-3 border rounded bg-light">
            <div v-if="currentRule.messageHtml" v-html="currentRule.messageHtml"></div>
            <em v-else class="text-muted">Message preview will appear here...</em>
          </div>
        </b-form-group>
      </b-form>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()" :disabled="saving">Cancel</b-button>
        <b-button variant="primary" @click="ok()" :disabled="saving">
          <b-spinner v-if="saving" small class="mr-1"></b-spinner>
          {{ isEditing ? 'Update Rule' : 'Add Rule' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
export default {
  name: 'AutoMessages',
  data() {
    return {
      modalId: 'auto-message-modal',
      isEditing: false,
      editingIndex: -1,
      currentRule: {
        conditionKey: '',
        whenTrigger: 'submitted',
        messageHtml: ''
      },
      tableFields: [
        { key: 'condition', label: 'Condition', sortable: true },
        { key: 'whenTrigger', label: 'When', sortable: true },
        { key: 'messagePreview', label: 'Message Preview' },
        { key: 'actions', label: 'Actions' }
      ],
      conditionOptions: [
        { value: '', text: 'Select a condition...', disabled: true },
        { value: 'act_as_aca', text: 'Act as ACA' },
        { value: 'aca_dcz', text: 'ACA DCZ' },
        { value: 'aca_full_directional', text: 'ACA Full Directional' },
        { value: 'aca_parking_removal', text: 'ACA Parking Removal' },
        { value: 'aca_turn_restrictions', text: 'ACA Turn Restrictions' },
        { value: 'crossing_permit_required', text: 'Crossing Permit Required' },
        { value: 'dcz_required', text: 'Designated Construction Zone Required' },
        { value: 'is_emergency', text: 'Emergency Request' },
        { value: 'is_under_two_hours', text: 'Is Under Two Hours' },
        { value: 'non_local_full_closure', text: 'Non-Local Full / Directional Closure' },
        { value: 'paid_parking_affected', text: 'Paid Parking Affected' },
        { value: 'painted_bicycle_closure', text: 'Painted Bicycle Lanes' },
        { value: 'painted_protected_bicycle_closure', text: 'Painted and Protected Bicycle Lanes' },
        { value: 'parking_removal_id', text: 'Parking Removal Required' },
        { value: 'permit_required', text: 'Permit Required' },
        { value: 'protected_bicycle_closure', text: 'Protected Bicycle Lanes' },
        { value: 'regional_closure', text: 'Regional Closure' },
        { value: 'regional_full_closure', text: 'Regional Full / Directional Closure' },
        { value: 'sidewalk_closure', text: 'Sidewalk Closure' },
        { value: 'temp_traffic_control', text: 'Temporary Traffic Control' },
        { value: 'traffic_signals_altered', text: 'Traffic Signals Altered' },
        { value: 'transit_affected', text: 'Transit Affected' },
      ],
      whenOptions: [
        { text: 'When Submitted', value: 'submitted' },
        { text: 'When Approved', value: 'approved' }
      ],
      emptyText: 'No auto message rules configured. Click "Add Auto Message Rule" to create your first rule.'
    }
  },
  computed: {
    ...mapState('autoMessages', ['rules', 'loading', 'saving']),
      safeRules() {
    return Array.isArray(this.rules) ? this.rules : []
  },
    formState() {
      return {
        conditionKey: this.currentRule.conditionKey ? true : false,
        whenTrigger: this.currentRule.whenTrigger ? true : false
        // ,messageHtml: this.currentRule.messageHtml && this.currentRule.messageHtml.trim() ? true : false
      }
    }
  },
  filters: {
    capitalize(value) {
      if (!value) return ''
      return value.charAt(0).toUpperCase() + value.slice(1)
    }
  },
  created() {
    console.log('[AutoMessages.vue] created hook fired')
    this.fetchRules()
  },
  methods: {
     ...mapActions('autoMessages', [
    'fetchAutoMessageRules',
    'saveAutoMessageRule', 
    'deleteAutoMessageRule'
  ]),
      async fetchRules() {
    try {
      await this.$store.dispatch('autoMessages/fetchAutoMessageRules')
    } catch (error) {
      this.showError(error.message)
    }
  },

    showAddModal() {
      this.isEditing = false
      this.editingIndex = -1
      this.resetForm()
      this.$bvModal.show(this.modalId)
    },

    editRule(rule) {
      this.isEditing = true
      this.currentRule = { ...rule }
      this.editingIndex = this.rules.findIndex(r => r.id === rule.id)
      this.$bvModal.show(this.modalId)
    },

async deleteRule(index) {
  const rule = this.rules[index]
  const confirmed = await this.$bvModal.msgBoxConfirm(
    'Are you sure you want to delete this auto message rule?',
    {
      title: 'Confirm Deletion',
      size: 'sm',
      okVariant: 'danger',
      okTitle: 'Delete',
      cancelTitle: 'Cancel',
      centered: true
    }
  )

  if (confirmed) {
    try {
      const result = await this.$store.dispatch('autoMessages/deleteAutoMessageRule', rule.id)
      await this.fetchRules()
      this.showSuccess(result.message)
    } catch (err) {
      console.error('Error deleting rule:', err)
      this.showError(err.message || 'Failed to delete rule.')
    }
  }
},


async saveRule() {
  if (!this.isFormValid()) {
    this.showWarning('Please fill in all required fields.')
    return
  }

  try {
    const payload = { ...this.currentRule }
    if (!this.isEditing) {
      delete payload.id
    }
    const result = await this.$store.dispatch('autoMessages/saveAutoMessageRule', {
      rule: payload,
      isEditing: this.isEditing
    })

    await this.fetchRules()
    this.$bvModal.hide(this.modalId)
    this.showSuccess(result.message)
  } catch (err) {
    console.error('Error saving rule:', err)
    this.showError(err.message || 'Failed to save rule. Please try again.')
  }
},


    isFormValid() {
      return this.currentRule.conditionKey && 
             this.currentRule.whenTrigger && 
             //this.currentRule.messageHtml &&
             this.currentRule.messageHtml.trim()
    },

    resetForm() {
      this.currentRule = {
        id: null,
        conditionKey: '',
        whenTrigger: 'submitted',
        messageHtml: ''
      }
      // Clear the WYSIWYG editor if it exists
      if (this.$refs.messageEditor) {
        this.$nextTick(() => {
          this.$refs.messageEditor.$el.innerHTML = ''
        })
      }
    },

    getConditionLabel(key) {
      const found = this.conditionOptions.find(opt => opt.value === key)
      return found ? found.text : key
    },

    getMessagePreview(html) {
      if (html == "") return '<em class="text-muted">No message</em>';
      if (!html) return '<em class="text-muted">No message</em>'
      const text = html.replace(/<[^>]*>/g, '').trim()
      return text.length > 100 ? text.slice(0, 100) + '...' : text
    },

    // Helper methods for toast messages
    showSuccess(message) {
      this.$bvToast.toast(message, {
        title: 'Success',
        variant: 'success',
        solid: true,
        toaster: 'b-toaster-top-right'
      })
    },

    showError(message) {
      this.$bvToast.toast(message, {
        title: 'Error',
        variant: 'danger',
        solid: true,
        toaster: 'b-toaster-top-right'
      })
    },

    showWarning(message) {
      this.$bvToast.toast(message, {
        title: 'Warning',
        variant: 'warning',
        solid: true,
        toaster: 'b-toaster-top-right'
      })
    }
  }
}
</script>

<style scoped>
.auto-messages-container {
  padding: 1rem 0;
}

.message-preview {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-editor-container {
  border: 1px solid #e9ecef;
  border-radius: 0.25rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
}

.message-preview-box {
  min-height: 60px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.custom-control-label {
  color: #000 !important;
}

.custom-control-input:checked ~ .custom-control-label::before,
.custom-control-input:not(:checked) ~ .custom-control-label::before {
  border-color: #000 !important;
  background-color: transparent !important;
}

.custom-control-input:checked ~ .custom-control-label::before {
  background-color: #000 !important;
}

.custom-control-input:focus ~ .custom-control-label::before {
  box-shadow: 0 0 0 0.2rem rgba(0, 0, 0, 0.25) !important;
}

.condition-label {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.9rem;
  background-color: #f9f9f9;
}

@media (max-width: 768px) {
  .message-preview {
    max-width: 200px;
  }
  
  .auto-messages-container {
    padding: 0.5rem 0;
  }
  
  h2 {
    font-size: 1.5rem;
  }
}

</style>